import { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";

import { PaymentMethod } from "common/types";

import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import PaymentForm from "client/payments/components/PaymentForm";
import { useClientQuery } from "client/accounts/hooks";
import { useCreatePaymentMethodMutation } from "client/payments/hooks";
import PaymentMethodSelectPanel from "client/payments/components/PaymentMethodSelectPanel";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";

import { usePatientCareBulkUpdateMutation } from "../hooks";

type CarePlanPaymentUpdateFormProps = {
  patientCareIds: number[];
  onConfirm: (isConfirmed: boolean) => void;
};

const CarePlanPaymentUpdateForm = (props: CarePlanPaymentUpdateFormProps) => {
  const { patientCareIds, onConfirm } = props;

  const [isAdding, setIsAdding] = useState(false);
  const { data: client, isLoading: clientLoading, error: clientError } = useClientQuery();

  const methods = client ? client.payment_methods : [];
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentMethod | undefined>(
    undefined,
  );
  const {
    mutate: createPaymentMethod,
    isLoading: isPaymentCreating,
    error: paymentCreateError,
  } = useCreatePaymentMethodMutation({
    onSuccess: (method) => {
      setSelectedPaymentOption(method);
      setIsAdding(false);
    },
  });

  const stripe = useStripe();

  const {
    mutate: updateCarePlanPayment,
    isLoading: isUpdateLoading,
    error: updateError,
  } = usePatientCareBulkUpdateMutation({
    onSuccess: () => {
      onConfirm(true);
    },
    onError: (error, data) => {
      if (!stripe || !error.data?.stripe_action_client_secret) {
        return;
      }
      stripe.handleCardAction(error.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          updateCarePlanPayment(data);
        }
      });
    },
  });

  const submitForm = () => {
    if (!selectedPaymentOption) {
      return;
    }

    updateCarePlanPayment({
      patient_cares: patientCareIds,
      payment_method: selectedPaymentOption.id,
    });
  };

  if (clientLoading) {
    return <ModalLayout.Loading />;
  }

  if (clientError) {
    return <ModalLayout.FetchError entityName="Payment method" error={clientError} />;
  }

  const declineError = updateError?.data?.card_decline_info;

  return (
    <>
      <ModalLayout.Form
        submitText="Authorize Payment"
        canSubmit={!!selectedPaymentOption}
        isLoading={isUpdateLoading}
        // Don't need to show generic error if there is a decline error
        error={declineError ? null : updateError}
        fieldErrors={[updateError?.errors?.payment_method]}
        onSubmit={submitForm}
      >
        {declineError && <CardPaymentDeclineAlert declineError={declineError} />}
        <PaymentMethodSelectPanel
          initialValue={undefined}
          value={selectedPaymentOption}
          options={methods}
          // Wallet not supported
          walletPayment={null}
          onAdd={() => setIsAdding(true)}
          onChange={(method) => {
            if (typeof method.id === "number") {
              // we don't have klarna or wallet payment methods here, so this code should always run
              setSelectedPaymentOption(method);
            }
          }}
        />
      </ModalLayout.Form>
      <Modal visible={isAdding} onClose={() => setIsAdding(false)}>
        <ModalLayout.Header title="Add new payment method" onClose={() => setIsAdding(false)} />
        <PaymentForm
          isLoading={isPaymentCreating}
          onSubmit={(pm_id) => {
            createPaymentMethod({ payment_method: pm_id });
          }}
          error={paymentCreateError}
        />
      </Modal>
    </>
  );
};

export default CarePlanPaymentUpdateForm;
